import { useState, useEffect } from "react"
import { useNavigate, useLocation } from "react-router-dom"
import { useLanguage } from "../contexts/LanguageContext"
import { ChevronDownIcon, Bars3Icon, XMarkIcon } from "@heroicons/react/24/outline"

export default function Header() {
  const {
    t,
    languages,
    currentLanguage,
    changeLanguage,
    isRTL,
    resetToGpsLanguage,
    isGpsDetectedLanguage,
    userHasManuallySelected
  } = useLanguage()
  const navigate = useNavigate()
  const location = useLocation()
  const [isLanguageOpen, setIsLanguageOpen] = useState(false)
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isScrolled, setIsScrolled] = useState(false)

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10)
    }
    window.addEventListener("scroll", handleScroll)
    return () => window.removeEventListener("scroll", handleScroll)
  }, [])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (isLanguageOpen && !event.target.closest('.language-selector')) {
        setIsLanguageOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isLanguageOpen])

  const scrollToSection = (sectionId) => {
    setIsMobileMenuOpen(false)

    // If we're not on the home page, navigate to home first
    if (location.pathname !== '/') {
      navigate('/')
      // Wait for navigation and DOM update, then scroll
      setTimeout(() => {
        const element = document.getElementById(sectionId)
        if (element) {
          element.scrollIntoView({ behavior: "smooth" })
        }
      }, 100)
    } else {
      // If we're already on home page, just scroll
      const element = document.getElementById(sectionId)
      if (element) {
        element.scrollIntoView({ behavior: "smooth" })
      }
    }
  }

  const menuItems = [
    { key: "home", section: "hero" },
    { key: "about", section: "about" },
    { key: "features", section: "features" },
    { key: "testimonials", section: "comments" },
    { key: "download", section: "download" },
  ]

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? "header shadow-lg" : "bg-white shadow-lg"
        }`}
      style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 50, backgroundColor: 'white', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)' }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <button
            onClick={() => navigate('/')}
            className={`flex items-center ${isRTL() ? 'space-x-reverse space-x-3' : 'space-x-3'} group cursor-pointer`}
          >
            <img
              src="https://hebbkx1anhila5yf.public.blob.vercel-storage.com/makkok%20%281%29.jpg-myVAdlMTUeB8dTRRf6pFwPSIWwvgrc.jpeg"
              alt="Makkok Logo"
              className="h-10 w-auto group-hover:scale-110 transition-transform duration-300"
            />
            <span className="text-2xl font-bold text-[#FF8500] group-hover:text-[#FF6B00] transition-colors duration-300">
              MAKKOK
            </span>
          </button>

          {/* Desktop Navigation */}
          <nav className={`hidden md:flex items-center ${isRTL() ? 'space-x-reverse space-x-8' : 'space-x-8'}`}>
            {menuItems.map((item, index) => (
              <button
                key={item.key}
                onClick={() => scrollToSection(item.section)}
                className="text-gray-700 hover:text-[#FF8500] font-medium transition-all duration-300 hover:scale-105 relative group"
                style={{ animationDelay: `${index * 100}ms` }}
              >
                {t(`nav.${item.key}`)}
                <span className="absolute bottom-0 left-0 w-0 h-0.5 bg-[#FF8500] group-hover:w-full transition-all duration-300"></span>
              </button>
            ))}
          </nav>

          {/* Language Selector & Mobile Menu */}
          <div
            className={`flex items-center ${isRTL() ? "space-x-reverse space-x-4" : "space-x-4"
              }`}
          >
            {/* Language Dropdown */}
            <div className="relative language-selector">
              <button
                onClick={() => setIsLanguageOpen(!isLanguageOpen)}
                className={`
        flex items-center ${isRTL() ? "space-x-reverse space-x-1" : "space-x-1"
                  } 
        text-gray-700 hover:text-[#FF8500] 
        transition-all duration-300 hover:scale-105
        px-2 py-1 rounded-md hover:bg-gray-50
        max-sm:px-3 max-sm:py-2
      `}
              >
                <span className="text-sm font-medium max-sm:text-base flex items-center">
                  {languages.find((lang) => lang.code === currentLanguage)?.name}
                  {isGpsDetectedLanguage() && (
                    <span
                      className="ml-1 text-xs text-green-600"
                      title="Language detected from GPS location"
                    ></span>
                  )}
                </span>
                <ChevronDownIcon
                  className={`h-4 w-4 max-sm:h-5 max-sm:w-5 transition-transform duration-300 ${isLanguageOpen ? "rotate-180" : ""
                    }`}
                />
              </button>

              {isLanguageOpen && (
                 <div
                   className={`
           absolute ${isRTL()
                       ? "left-0"
                       : "right-0"
                     } 
           mt-2 w-52 sm:w-56 md:w-52
           bg-white rounded-md shadow-xl border border-gray-200 py-1 
           z-[9999] animate-fadeInUp 
           max-h-64 overflow-y-auto overflow-x-hidden
           
           max-sm:fixed max-sm:left-4 max-sm:right-4 max-sm:w-auto 
           max-sm:top-16 max-sm:max-h-[60vh] max-sm:overflow-x-hidden
           max-sm:shadow-2xl max-sm:border-2
           
           scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-gray-100
         `}
                 >
                  {languages.map((language, index) => (
                    <button
                      key={language.code}
                      onClick={() => {
                        changeLanguage(language.code);
                        setIsLanguageOpen(false);
                      }}
                      className={`
              block w-full px-4 py-3 text-sm font-medium
              transition-all duration-200 
              hover:bg-gray-100 active:bg-gray-200
              ${isRTL() ? "text-right" : "text-left"}
              ${isRTL() ? "hover:-translate-x-1" : "hover:translate-x-1"}
              ${currentLanguage === language.code
                          ? `text-[#FF8500] bg-orange-50 ${isRTL()
                            ? "border-r-4 border-[#FF8500]"
                            : "border-l-4 border-[#FF8500]"
                          }`
                          : "text-gray-700"
                        }
              max-sm:py-4 max-sm:text-base
              whitespace-nowrap overflow-hidden text-ellipsis
            `}
                      style={{ animationDelay: `${index * 50}ms` }}
                    >
                      {language.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden text-gray-700 hover:text-[#FF8500] transition-all duration-300 hover:scale-110"
            >
              {isMobileMenuOpen ? (
                <XMarkIcon className="h-6 w-6" />
              ) : (
                <Bars3Icon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4 animate-fadeInUp">
            <nav className="flex flex-col space-y-4">
              {menuItems.map((item, index) => (
                <button
                  key={item.key}
                  onClick={() => scrollToSection(item.section)}
                  className={`${isRTL() ? 'text-right' : 'text-left'} text-gray-700 hover:text-[#FF8500] font-medium transition-all duration-300 ${isRTL() ? 'hover:-translate-x-2' : 'hover:translate-x-2'}`}
                  style={{ animationDelay: `${index * 100}ms` }}
                >
                  {t(`nav.${item.key}`)}
                </button>
              ))}
            </nav>

            {/* Mobile Language Selector */}
            <div className="mt-4 pt-4 border-t border-gray-200">
              <h3 className={`text-sm font-medium text-gray-500 mb-2 ${isRTL() ? 'text-right' : 'text-left'}`}>
                Language / اللغة
              </h3>
              <div className="grid grid-cols-2 gap-2">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => {
                      changeLanguage(language.code)
                      setIsMobileMenuOpen(false)
                    }}
                    className={`p-2 text-sm rounded-md transition-all duration-200 ${currentLanguage === language.code
                        ? "bg-[#FF8500] text-white"
                        : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      } ${isRTL() ? 'text-right' : 'text-left'}`}
                  >
                    {language.name}
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </header>
  )
}
